import {
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/solid';
import { ArrowTopRightOnSquareIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useRef, useState } from "react";
import { getSupabaseClient } from "../lib/supabase";

const ACCEPTED = ["application/pdf", "image/png", "image/jpeg"];
const MAX_MB = 10;
const BUCKET = "background-checks";
const NEXT_PATH = "/onboarding/complete-profile";

export default function BackgroundCheck() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const pickFile = () => inputRef.current?.click();

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;

    if (!ACCEPTED.includes(f.type)) {
      alert("Only PDF, JPG, or PNG files are accepted.");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      alert("File must be ≤ 10MB.");
      return;
    }
    setFile(f);
  };

  const handleUploadAndSave = async () => {
    if (!file) {
      alert("Please choose a file first.");
      return;
    }
    setBusy(true);
    try {
      const supabase = getSupabaseClient();

      const {
        data: { user },
        error: userErr,
      } = await supabase.auth.getUser();
      if (userErr) throw userErr;
      if (!user) {
        alert("Not logged in.");
        return;
      }

      const safeName = file.name.replace(/[^\w.\-]+/g, "_");
      const objectKey = `guides/${user.id}/${Date.now()}-${safeName}`;

      // Check if user has a profile (required for foreign key)
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      if (!profile) {
        // Create profile if it doesn't exist
        const { error: profileErr } = await supabase.from('profiles').insert({
          id: user.id,
          email: user.email,
          role: 'guide',
          full_name: user.user_metadata?.full_name || '',
        });
        
        if (profileErr) {
          console.error('Failed to create profile:', profileErr);
          alert("Failed to create profile. Please contact support.");
          return;
        }
      }

      // 1) Upload to Storage (bucket: background-checks)
      const { error: upErr } = await supabase
        .storage
        .from(BUCKET)
        .upload(objectKey, file, { upsert: true });
      if (upErr) throw upErr;

      // 2) Insert record 
      const { error: insErr } = await supabase
        .from("guide_background_checks")
        .insert({
          user_id: user.id,
          storage_object_key: objectKey,
          storage_bucket: BUCKET,
          original_filename: file.name,
          status: "uploaded",
          file_path: objectKey,
        });
      if (insErr) throw insErr;

      navigate(NEXT_PATH);
    } catch (e: any) {
      console.error(e);
      alert(e?.message ?? "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fffcef] px-4 py-10">
      <div className="mx-auto max-w-3xl text-[#304031]">
        {/* Header */}
        <h1 className="mb-3 text-center font-recoleta text-[42px] leading-[44px] font-medium text-[#304031]">
          Get a Verified Badge
        </h1>
        <p className="mb-2 text-center text-2xl font-medium text-[#304031]">
          Criminal Background Check
        </p>
        <p className="mb-12 text-center text-lg text-[#304031]/70">
          Upload your criminal background check to complete the verification process.
        </p>

        {/* Upload Section */}
        <div className="bg-[#fffcef] border-2 border-[#a0978a] rounded-2xl p-8 mb-8 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <p className="text-xl font-semibold text-[#304031]">Background Check Document</p>
            <span className="text-xs font-semibold uppercase tracking-wide text-[#304031]/50 border-2 border-[#304031]/20 px-3 py-1 rounded-full">
              Optional
            </span>
          </div>

          <div
            className="border-2 border-dashed border-[#a0978a] rounded-2xl py-12 text-center cursor-pointer hover:bg-[#304031]/5 transition-colors"
            onClick={pickFile}
          >
            <CloudArrowUpIcon className="h-12 w-12 mx-auto mb-4 text-[#304031]/40" />
            <p className="text-[#304031]/70">
              {file ? (
                <span className="text-[#304031] font-medium">{file.name}</span>
              ) : (
                <>
                  Drop your file here, or{" "}
                  <span className="text-[#304031] underline cursor-pointer font-medium">browse</span>
                </>
              )}
            </p>
            <p className="text-xs mt-2 text-[#304031]/60">PDF, JPG, PNG files up to 10MB</p>

            <input
              ref={inputRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={onFile}
            />
          </div>
        </div>

        {/* Resources Section */}
        <div className="bg-[#fffcef] border-2 border-[#a0978a] rounded-2xl p-8 mb-8 shadow-sm">
          <p className="text-xl font-semibold text-[#304031] mb-3">Acceptable Background Check Resources</p>
          <p className="text-sm text-[#304031]/70 mb-6">
            Don't have a background check yet? You can obtain one from these trusted providers:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                title: 'FBI Identity History Summary',
                desc: 'Official FBI background check for federal records.',
                time: 'Processing: 3–5 days',
                link: 'https://www.fbi.gov/services/cjis/identity-history-summary-checks',
              },
              {
                title: 'Checkr',
                desc: 'Comprehensive background screening service.',
                time: 'Processing: 1–3 days',
                link: 'https://checkr.com/background-check?variant=Personal',
              },
              {
                title: 'Sterling Volunteers',
                desc: 'Background checks for volunteer and professional use.',
                time: 'Processing: 2–4 days',
                link: 'https://www.sterlingvolunteers.com/getavolunteerbackgroundcheck/',
              },
              {
                title: 'GoodHire',
                desc: 'Fast and accurate background screening.',
                time: 'Processing: 1–2 days',
                link: 'https://www.goodhire.com/personal-background-check/',
              },
            ].map((r) => (
              <div
                key={r.title}
                className="border-2 border-[#a0978a] rounded-xl p-5 flex flex-col justify-between bg-[#fffcef] hover:border-[#304031] transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <p className="font-semibold text-[#304031]">{r.title}</p>
                  <ArrowTopRightOnSquareIcon className="h-5 w-5 text-[#304031]/40 flex-shrink-0 ml-2" />
                </div>

                <div>
                  <p className="text-sm text-[#304031]/70 mb-3">{r.desc}</p>
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-xs text-[#304031]/60">{r.time}</p>
                  <a
                    href={r.link}
                    className="text-sm text-[#304031] underline font-medium hover:text-[#304031]/80"
                  >
                    Visit Site
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Requirements Box */}
        <div className="bg-[#304031]/5 border-2 border-[#a0978a] rounded-2xl p-6 flex gap-4 mb-6 text-sm">
          <ShieldExclamationIcon className="h-6 w-6 text-[#304031] mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-[#304031] mb-3">Background Check Requirements:</p>
            <ul className="space-y-2 text-[#304031]/70">
              <li className="flex items-start gap-2">
                <span className="text-[#304031] font-bold">•</span>
                <span>Document must be issued within the last 12 months</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#304031] font-bold">•</span>
                <span>Must show criminal history for all jurisdictions where you've lived</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#304031] font-bold">•</span>
                <span>Document must be official and include issuing authority information</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#304031] font-bold">•</span>
                <span>International applicants may submit equivalent documents from their country</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#304031] font-bold">•</span>
                <span>All information is kept strictly confidential and secure</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Important Notice */}
        <div className="bg-[#FFF3D9] border-2 border-[#C47C16]/30 rounded-2xl p-6 flex gap-4 mb-8">
          <ExclamationTriangleIcon className="h-7 w-7 text-[#C47C16] mt-0.5 flex-shrink-0" />
          <div className="text-sm text-[#8B6914]">
            <p className="font-semibold text-[#8B6914] mb-2">Important Notice:</p>
            <p>
              Having a criminal record does not automatically disqualify you from becoming a guide. Each case is reviewed individually, and we consider factors such as the nature of the offense, time elapsed, and evidence of rehabilitation.
            </p>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-4 items-center justify-between">
          <button
            onClick={() => navigate('/onboarding/complete-profile')}
            disabled={busy}
            className="inline-flex items-center gap-2 border-2 border-[#304031] px-6 py-3 rounded-full hover:bg-[#304031]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-[#304031]"
          >
            ← Back
          </button>
          <button
            onClick={handleUploadAndSave}
            disabled={!file || busy}
            className={`inline-flex items-center gap-2 px-8 py-3 rounded-full font-semibold transition ${
              !file || busy
                ? 'bg-[#304031]/20 text-[#304031]/40 cursor-not-allowed'
                : 'bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90'
            }`}
          >
            {busy ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-[#fef6d6] border-t-transparent rounded-full"></div>
                Uploading...
              </>
            ) : (
              'Upload & Continue →'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
